import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Edit3, FileText, Info, Layout, Clock, User, BarChart3 } from 'lucide-react';
import { useTemplatesDetail } from './hooks/useTemplatesDetail';
import { cn } from '@/utils/cn';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';

export function TemplatesDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { template, isLoading } = useTemplatesDetail(id!);

  if (isLoading) {
    return <div className="p-8 text-center text-slate-500 animate-pulse font-medium">Đang tải chi tiết mẫu...</div>;
  }

  if (!template) {
    return <div className="p-8 text-center text-slate-500 font-medium">Không tìm thấy mẫu tài liệu</div>;
  }

  const statusMap = {
    active: { label: 'Công khai', color: 'bg-emerald-500', text: 'text-emerald-600', bg: 'bg-emerald-50' },
    archived: { label: 'Lưu trữ', color: 'bg-slate-400', text: 'text-slate-500', bg: 'bg-slate-50' },
    draft: { label: 'Nháp', color: 'bg-amber-500', text: 'text-amber-600', bg: 'bg-amber-50' },
  };

  const typeMap = {
    report: 'Báo cáo',
    workflow: 'Quy trình',
    form: 'Biểu mẫu',
    other: 'Khác',
  };

  return (
    <div className="w-full flex-1 min-w-0 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 rounded-md border border-slate-100 bg-white p-3 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('?tab=templates')}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 active:scale-95 transition-all"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-lg font-medium text-slate-900">{template.name}</h1>
            <p className="text-sm text-slate-500">{template.code}</p>
          </div>
        </div>
        <button
          onClick={() => navigate(`?tab=templates-edit&id=${template.id}`)}
          className="flex h-10 items-center gap-2 rounded-md bg-emerald-700 px-4 text-sm font-medium text-white shadow-md shadow-emerald-700/10 hover:bg-emerald-800 transition-all active:scale-95"
        >
          <Edit3 className="h-4 w-4" />
          <span>Chỉnh sửa</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="md:col-span-2 space-y-4">
          <div className="rounded-md border border-slate-100 bg-white p-4 shadow-sm space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-50 pb-2 text-slate-800 font-medium">
              <Info className="h-4 w-4 text-emerald-600" />
              <span>Tổng quan mẫu tài liệu</span>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-1">
                <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Mô tả</p>
                <p className="text-sm text-slate-700 leading-relaxed">
                  {template.description || 'Chưa có mô tả cho mẫu tài liệu này.'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="rounded-md bg-slate-50 p-3 space-y-1">
                  <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Phân loại</p>
                  <p className="text-sm font-medium text-slate-700">{typeMap[template.type]}</p>
                </div>
                <div className="rounded-md bg-slate-50 p-3 space-y-1">
                  <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Trạng thái</p>
                  <div className={cn('inline-flex items-center gap-1.5 text-sm font-medium', statusMap[template.status].text)}>
                    <div className={cn('h-1.5 w-1.5 rounded-full', statusMap[template.status].color)} />
                    {statusMap[template.status].label}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Usage Statistics Placeholder */}
          <div className="rounded-md border border-slate-100 bg-white p-4 shadow-sm space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-50 pb-2 text-slate-800 font-medium">
              <BarChart3 className="h-4 w-4 text-emerald-600" />
              <span>Thống kê sử dụng</span>
            </div>
            <div className="flex items-center justify-between p-4 rounded-md bg-emerald-50/50 border border-emerald-50">
               <div className="space-y-1">
                  <p className="text-sm text-emerald-800 font-medium">Tổng số lượt áp dụng</p>
                  <p className="text-xs text-emerald-600">Dữ liệu được cập nhật thời gian thực</p>
               </div>
               <p className="text-3xl font-medium text-emerald-700">{template.usageCount}</p>
            </div>
          </div>
        </div>

        {/* Sidebar Info */}
        <div className="space-y-4">
          <div className="rounded-md border border-slate-100 bg-white p-4 shadow-sm space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-50 pb-2 text-slate-800 font-medium">
              <Clock className="h-4 w-4 text-emerald-600" />
              <span>Lịch sử</span>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                  <User className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Người tạo</p>
                  <p className="text-sm font-medium text-slate-700 truncate">Quản trị viên</p>
                </div>
              </div>

              <div className="space-y-1 pl-11 border-l-2 border-slate-50 ml-4 pb-2">
                <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Ngày tạo</p>
                <p className="text-sm text-slate-600">
                  {format(new Date(template.createdAt), 'dd MMMM, yyyy', { locale: vi })}
                </p>
              </div>

              <div className="space-y-1 pl-11 border-l-2 border-slate-50 ml-4">
                <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Cập nhật cuối</p>
                <p className="text-sm text-slate-600">
                   {format(new Date(template.updatedAt), 'dd MMMM, yyyy', { locale: vi })}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-md bg-blue-50 p-4 border border-blue-100">
            <h4 className="text-xs font-medium text-blue-800 mb-1">Mẹo nhỏ</h4>
            <p className="text-xs text-blue-600 leading-relaxed">
              Các mẫu tài liệu giúp chuẩn hóa quy trình làm việc và tiết kiệm 40% thời gian nhập liệu cho nhân viên.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
